import React from "react";
import styled from "styled-components";
import { Row, SizedBox } from "../CommonBaseComponents";
import CustomIcon from "../../../common/components/image/CustomIcon";
import { C3Text, C4Text } from "../text/CustomText";
import { Theme } from "../../Theme";

const TableContainer = styled.div`
    width: 100%;
    overflow-x: auto;
    font-family: "Pretendard", sans-serif;
    background-color: ${Theme.color.white};
    border-radius: 8px;
    border: 1px solid ${Theme.color.gray200};
`;

const StyledTable = styled.table`
    width: 100%;
    min-width: ${(props) => props.minWidth || "800px"};
    border-collapse: collapse;
    box-shadow: none;
    font-size: 13px;
    text-align: center;
`;

const StyledThead = styled.thead`
    background-color: ${Theme.color.neutral100};
    color: ${Theme.color.gray500};
    font-weight: 500;
`;

const StyledTbody = styled.tbody`
    color: ${Theme.color.zinc850};
    font-weight: 400;
`;

const StyledTr = styled.tr`
    border-bottom: 1px solid ${Theme.color.gray200};

    &:hover {
        background-color: ${Theme.color.gray50};
    }
    &:last-child {
        border-bottom: none;
    }
`;

const StyledTh = styled.th`
    padding: 9px 16px;
    font-weight: 500;
    text-transform: capitalize;
    border-bottom: 1px solid ${Theme.color.slate100};
    white-space: nowrap;
    border-right: ${(props) => (props.hasColumnBorder ? `1px solid ${Theme.color.slate100}` : "none")};
    &:last-child {
        border-right: none;
    }
`;

const StyledTd = styled.td`
    padding: 9px 16px;
    text-align: ${(props) => props.textAlign || "center"};
    white-space: ${(props) => props.whiteSpace || "nowrap"};
    min-width: ${(props) => props.minWidth || "none"};
    max-width: ${(props) => props.maxWidth || "auto"};
    width: ${(props) => props.maxWidth || "auto"};
    border-right: ${(props) => (props.hasColumnBorder ? `1px solid ${Theme.color.slate100}` : "none")};
    &:last-child {
        border-right: none;
    }
`;

/**
 * @param {Array} headers - { key, label, render? } 객체의 배열
 * @param {Array} data - 데이터 객체의 배열
 * @param {Array} hasColumeBorder - 열 보더 존재 여부
 */
export const CustomTable = ({ headers = [], data = [], hasColumnBorder = true, minWidth }) => {
    return (
        <TableContainer>
            <StyledTable minWidth={minWidth}>
                <StyledThead>
                    <tr>
                        {headers.map((header) => (
                            <StyledTh key={header.key} hasColumnBorder={hasColumnBorder}>
                                <Row
                                    alignItems="center"
                                    justifyContent="center"
                                    cursor={header.onClick !== undefined ? "pointer" : undefined}
                                    gap="4px"
                                    onClick={header.onClick}
                                >
                                    {header.label}
                                    {header.hasSortButton && (
                                        <CustomIcon iconName="arrow_switch_horizontal" size="18px" cursor="pointer" />
                                    )}
                                </Row>
                            </StyledTh>
                        ))}
                    </tr>
                </StyledThead>
                <StyledTbody>
                    {data.map((row, rowIndex) => (
                        <StyledTr key={rowIndex}>
                            {headers.map((header) => (
                                <StyledTd
                                    key={header.key}
                                    hasColumnBorder={hasColumnBorder}
                                    textAlign={header.textAlign}
                                    whiteSpace={header.whiteSpace}
                                    minWidth={header.minWidth}
                                    maxWidth={header.maxWidth}
                                >
                                    {header.render ? header.render(row, rowIndex) : row[header.key]}
                                </StyledTd>
                            ))}
                        </StyledTr>
                    ))}
                </StyledTbody>
            </StyledTable>
        </TableContainer>
    );
};

export const LinkTd = styled.a`
    color: #6250fc;
    font-weight: 500;
    text-decoration: ${({ $noUnderLine }) => ($noUnderLine ? "none" : "underline")};
`;

export const StatusBadge = styled.span`
    display: inline-block;
    padding: 4px 10px;
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1.2;
    color: ${({ color }) => color ?? Theme.color.gray700};
    background-color: ${({ backgroundColor }) => backgroundColor ?? Theme.color.gray100};
    border-radius: 9999px;
`;

export const ShopStatus = ({ status }) => {
    if (status === "Open") {
        return (
            <StatusBadge backgroundColor={Theme.color.neutral100}>
                <Row>
                    <SizedBox
                        width={"6px"}
                        height={"6px"}
                        borderRadius={"100%"}
                        backgroundColor={Theme.color.green600}
                    />
                    <SizedBox width={"6px"} />
                    {status}
                </Row>
            </StatusBadge>
        );
    } else {
        return (
            <StatusBadge backgroundColor={Theme.color.neutral100}>
                <Row>
                    <SizedBox
                        width={"6px"}
                        height={"6px"}
                        borderRadius={"100%"}
                        backgroundColor={Theme.color.gray400}
                    />
                    <SizedBox width={"6px"} />
                    {status}
                </Row>
            </StatusBadge>
        );
    }
};

export const ResultStatus = ({ children }) => {
    if (children === "Failed") {
        return (
            <Row alignItems="center" justifyContent="center">
                <CustomIcon iconName="information_circle_contained" />
                <SizedBox width="4px" />
                <ResultText>{children}</ResultText>
            </Row>
        );
    }

    return <ResultText>{children}</ResultText>;
};

export const UserRequestStatusBadge = ({ children }) => {
    let backgroundColor;
    let textColor;

    if (children === "Pending") {
        backgroundColor = Theme.color.indigo500_007;
        textColor = Theme.color.indigo500;
    } else if (children === "Reject") {
        backgroundColor = Theme.color.rose500_007;
        textColor = Theme.color.rose500;
    } else {
        backgroundColor = Theme.color.teal600_007;
        textColor = Theme.color.teal600;
    }

    return (
        <StatusBadge backgroundColor={backgroundColor}>
            <C4Text fontColor={textColor} fontWeight={"400"}>
                {children}
            </C4Text>
        </StatusBadge>
    );
};

export const EventRow = ({ children }) => {
    if (children === "Error") {
        return (
            <Row alignItems="center" justifyContent="center">
                <CustomIcon iconName="alert_triangle" />
                <SizedBox width="4px" />
                <C3Text fontColor={Theme.color.rose500}>{children}</C3Text>
            </Row>
        );
    }

    return <ResultText>{children}</ResultText>;
};

const ResultText = styled.span`
    color: ${({ children }) => (children === "Failed" ? Theme.color.rose500 : Theme.color.zinc850)};
`;
